import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Clock, CreditCard, Loader2, MapPin, QrCode, RefreshCw, Sparkles, Ticket, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmtDate, rupiah } from "@/lib/api";
import { EventBadge } from "@/components/landing/Community";
import { PageTitle, useApi, Skeleton, Stagger, Item, btnPrimary } from "@/components/portal/ui";
import { CoverImage } from "@/components/CoverImage";

const CHIPS = [["all", "Semua"], ["ibadah", "Ibadah"], ["youth", "Youth"], ["retreat", "Retreat"], ["umum", "Umum"]];

function TicketDialog({ event, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="event-ticket-dialog" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5">
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-amber-300">E-Ticket GIM Church</p>
          <p className="mt-2 text-lg font-black leading-tight">{event.title}</p>
          <p className="text-xs text-white/60 mt-1">{fmtDate(event.start_date)} · {event.time} WITA · {event.location}</p>
        </div>
        <div className="p-6 text-center">
          <div className="inline-block bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
            <QRCodeSVG value={`GIM-TIX:${event.ticket_code}`} size={180} data-testid="event-ticket-qr" />
          </div>
          <p className="mt-4 font-black tracking-widest text-slate-900" data-testid="event-ticket-code">{event.ticket_code}</p>
          <p className="text-xs text-slate-500 mt-1">{event.ticket_type || "Tiket Reguler"} · {(event.ticket_price || 0) > 0 ? rupiah(event.ticket_price) : "Gratis"}</p>
          {event.scanned_at ? (
            <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold bg-slate-100 text-slate-600 rounded-full px-3 py-1.5" data-testid="event-ticket-used"><CheckCircle2 className="w-3.5 h-3.5" />Sudah discan panitia</p>
          ) : (
            <p className="mt-4 text-xs text-slate-400">Tunjukkan QR ini kepada panitia di pintu masuk.</p>
          )}
          <button onClick={onClose} data-testid="event-ticket-close" className="w-full mt-5 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-700">Tutup</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RegisterDialog({ event, onClose, onDone }) {
  const types = event.ticket_types || [];
  const [cfg, setCfg] = useState(null);
  const [gateway, setGateway] = useState(null);
  const [ticket, setTicket] = useState(types[0]?.name || null);
  const [loading, setLoading] = useState(false);
  const price = ticket ? (types.find((t) => t.name === ticket)?.price ?? 0) : (event.price || 0);

  useEffect(() => {
    api.get("/portal/payment-config").then((r) => { setCfg(r.data); setGateway(r.data.gateways[0] || null); }).catch((e) => toast.error(errMsg(e)));
  }, []);

  const go = async () => {
    if (price > 0 && !gateway) return toast.error("Belum ada pembayaran online aktif. Hubungi admin.");
    setLoading(true);
    try {
      const { data } = await api.post(`/portal/events/${event.id}/register`,
        { ticket_type: ticket, ...(price > 0 ? { gateway, return_url: window.location.href } : {}) });
      if (data.payment_required && data.payment_url) {
        toast.success("Mengalihkan ke halaman pembayaran…");
        window.location.href = data.payment_url;
      } else {
        toast.success("Pendaftaran berhasil. Tiket QR sudah terbit!");
        onDone();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="event-register-dialog">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div><h2 className="text-lg font-black text-slate-900">Registrasi Event</h2><p className="text-xs text-slate-500 mt-1">{event.title}</p></div>
          <button onClick={onClose} data-testid="event-register-close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {types.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="ticket-type-list">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Jenis Tiket</p>
            {types.map((t) => (
              <button key={t.name} onClick={() => setTicket(t.name)} data-testid={`ticket-type-${t.name}`}
                className={`w-full flex items-center gap-3 rounded-2xl border p-4 text-left transition active:scale-[0.98] ${ticket === t.name ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                <Ticket className={`w-5 h-5 ${ticket === t.name ? "text-blue-600" : "text-slate-400"}`} />
                <span className="flex-1 min-w-0"><span className="block font-bold text-sm text-slate-900">{t.name}</span>{t.quota > 0 && <span className="block text-[11px] text-slate-400">Kuota {t.quota}</span>}</span>
                <span className="shrink-0 text-sm font-black text-slate-900">{t.price > 0 ? rupiah(t.price) : "Gratis"}</span>
              </button>
            ))}
          </div>
        )}

        <p className="mt-5 text-3xl font-black text-slate-900" data-testid="event-register-total">{price > 0 ? rupiah(price) : "Gratis"}</p>

        {price > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Metode Pembayaran</p>
            {!cfg ? <p className="text-sm text-slate-400">Memuat metode…</p> : cfg.gateways.length === 0 ? (
              <p className="text-sm font-semibold text-amber-700 bg-amber-50 rounded-xl px-3 py-3" data-testid="event-pay-unavailable">Pembayaran online belum diaktifkan admin. Silakan hubungi OPC.</p>
            ) : cfg.gateways.map((g) => (
              <button key={g} onClick={() => setGateway(g)} data-testid={`event-gateway-${g}`} className={`w-full flex items-center gap-3 rounded-2xl border p-4 text-left transition active:scale-[0.98] ${gateway === g ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                <CreditCard className={`w-5 h-5 ${gateway === g ? "text-blue-600" : "text-slate-400"}`} />
                <span className="font-bold text-sm text-slate-800 capitalize">{g}</span>
                <span className="ml-auto text-[11px] text-slate-400">{g === "xendit" ? "QRIS · VA Bank" : "QRIS · VA · E-Wallet"}</span>
              </button>
            ))}
          </div>
        )}

        <button onClick={go} disabled={loading || (price > 0 && !cfg?.gateways?.length)} data-testid="event-register-submit" className={`${btnPrimary} w-full mt-5`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : price > 0 ? "Bayar & Dapatkan Tiket" : "Daftar & Dapatkan Tiket"}
        </button>
      </div>
    </div>
  );
}

function EventCard({ e, onCancel, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const pending = e.reg_status === "pending_payment";
  const full = e.quota && e.registrations >= e.quota && !e.registered && !pending;

  const click = async () => {
    if (pending) {
      if (e.payment_url) window.location.href = e.payment_url;
      return;
    }
    if (e.registered) {
      setLoading(true);
      await onCancel(e);
      setLoading(false);
      return;
    }
    setRegOpen(true);
  };
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/portal/events/${e.id}/registration/refresh`);
      if (data.status === "active") {
        toast.success("Pembayaran terkonfirmasi. Tiket QR kamu aktif!");
        onRefresh();
      } else {
        toast(`Status: ${data.status === "pending_payment" ? "menunggu pembayaran" : data.status}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <article className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden hover:-translate-y-1 transition duration-300" data-testid={`event-card-${e.id}`}>
      <div className="relative h-40">
        <CoverImage src={fileUrl(e.image_url_portal || e.image_url)} alt={e.title} className="w-full h-full" />
        <span className="absolute top-4 left-4 flex gap-2"><EventBadge e={e} />{e.special && <span className="text-[10px] font-black uppercase tracking-wider bg-amber-400 text-slate-900 rounded-full px-2.5 py-1 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />Khusus</span>}</span>
        <div className="absolute top-4 right-4 bg-white rounded-2xl px-3 py-1.5 text-center shadow"><p className="text-lg font-black text-slate-900 leading-none">{fmtDate(e.start_date, { day: "2-digit" })}</p><p className="text-[10px] font-bold text-blue-600 uppercase">{fmtDate(e.start_date, { month: "short" })}</p></div>
      </div>
      <div className="p-5">
        <p className="font-extrabold text-slate-900">{e.title}</p>
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-3"><span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{e.time} WITA</span><span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.location}</span></p>
        {e.ticket_types?.length > 0 && <p className="text-[11px] text-slate-400 mt-2">{e.ticket_types.map((t) => `${t.name} ${t.price > 0 ? rupiah(t.price) : "Gratis"}`).join(" · ")}</p>}
        {e.quota > 0 && !e.no_registration && <p className="text-xs text-slate-500 mt-1">{e.registrations}/{e.quota} peserta</p>}
        {e.no_registration ? (
          <p className="mt-4 text-xs font-bold text-slate-600 bg-slate-50 rounded-xl px-3 py-3 text-center" data-testid={`event-no-reg-${e.id}`}>Ibadah/pengumuman — tanpa registrasi. Datang & bawa keluargamu!</p>
        ) : (
        <button onClick={click} disabled={loading || full} data-testid={`event-register-${e.id}`} className={`${btnPrimary} w-full mt-4 !py-3 ${e.registered ? "!bg-emerald-500 hover:!bg-emerald-600" : pending ? "!bg-amber-500 hover:!bg-amber-600" : ""}`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : full ? "Kuota Penuh" : pending ? "Lanjutkan Pembayaran" : e.registered ? "Terdaftar ✓ · Batalkan" : (e.price || 0) > 0 || e.ticket_types?.some((t) => t.price > 0) ? "Daftar & Bayar" : "Daftar Sekarang"}
        </button>
        )}
        {e.registered && e.ticket_code && (
          <button onClick={() => setTicketOpen(true)} data-testid={`event-ticket-button-${e.id}`} className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl py-3 text-sm font-bold active:scale-95 transition"><QrCode className="w-4 h-4" />Lihat Tiket QR</button>
        )}
        {pending && (
          <button onClick={refresh} disabled={loading} data-testid={`event-refresh-${e.id}`} className="w-full mt-2 text-xs font-bold text-blue-600 inline-flex items-center justify-center gap-1.5 py-2"><RefreshCw className="w-3.5 h-3.5" /> Cek status pembayaran</button>
        )}
      </div>
      {regOpen && <RegisterDialog event={e} onClose={() => setRegOpen(false)} onDone={() => { setRegOpen(false); onRefresh(); }} />}
      {ticketOpen && <TicketDialog event={e} onClose={() => setTicketOpen(false)} />}
    </article>
  );
}

export default function PortalEvents() {
  const { data, reload } = useApi("/portal/events");
  const [chip, setChip] = useState("all");
  const onCancel = async (e) => {
    try {
      await api.delete(`/portal/events/${e.id}/register`);
      toast("Pendaftaran dibatalkan");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const items = (data || []).filter((e) => chip === "all" || e.category === chip);

  return (
    <div data-testid="portal-events">
      <PageTitle eyebrow="Agenda" title="Events" />
      <div className="mt-4 px-5 flex gap-2 overflow-x-auto hide-scroll">
        {CHIPS.map(([v, l]) => (
          <button key={v} onClick={() => setChip(v)} data-testid={`events-chip-${v}`} className={`shrink-0 text-sm rounded-full px-4 py-2 transition active:scale-95 ${chip === v ? "bg-blue-600 text-white font-bold" : "bg-white border border-slate-200 text-slate-600 font-semibold"}`}>{l}</button>
        ))}
      </div>
      <div className="px-5 mt-5">
        {!data ? <Skeleton className="h-72" /> : (
          <Stagger key={chip} className="space-y-5">
            {items.length === 0 && <p className="text-sm text-slate-500" data-testid="events-empty">Belum ada kegiatan di kategori ini.</p>}
            {items.map((e) => <Item key={e.id}><EventCard e={e} onCancel={onCancel} onRefresh={reload} /></Item>)}
          </Stagger>
        )}
      </div>
    </div>
  );
}
